import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, X, Loader2, Trash2, Eye, ChevronLeft, ChevronRight, Send, Volume2, VolumeX, Sparkles, MessageSquare, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "https://lostdrkc.in").replace(/\/$/, "");
function abs(url) {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/api/files/lostdrkc-live/")) {
    return `https://creators.lostdrkc.in${url}`;
  }
  return `${BACKEND}${url.startsWith("/") ? "" : "/"}${url}`;
}

/* ------------------------------------------------------------------ */
/* Create-story modal                                                  */
/* ------------------------------------------------------------------ */
function CreateStory({ onClose, onCreated }) {
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [media, setMedia] = useState(null);
  const [caption, setCaption] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);

  const isCreator = !!user?.is_creator;

  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const isVideo = f.type.startsWith("video/");
    const isImage = f.type.startsWith("image/");
    if (!isVideo && !isImage) return toast.error("Pick an image or video");
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", f);
      form.append("kind", isVideo ? "video" : "image");
      const { data } = await api.post("/stories/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setMedia({ url: data.url, kind: data.media_kind || data.kind || (isVideo ? "video" : "image") });
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!media) return toast.error("Add a photo or video first");
    setPosting(true);
    try {
      await api.post("/stories", {
        media_url: media.url,
        media_kind: media.kind,
        caption,
        visibility: isCreator ? visibility : "public",
      });
      toast.success("Story added successfully");
      onCreated?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to post story"); }
    finally { setPosting(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="create-story-modal">
      <motion.div initial={{ y: 30, opacity: 0, scale: 0.95 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 20, opacity: 0, scale: 0.95 }}
        className="glass-strong w-full max-w-md rounded-3xl p-6 border border-white/10 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-bold flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#FF6AD5]" /> Add Story
          </h3>
          <button onClick={onClose} className="p-2 rounded-full glass glass-hover"><X className="w-4 h-4" /></button>
        </div>
        <input ref={fileRef} type="file" accept="image/*,video/*" onChange={pick} className="hidden" data-testid="story-file-input" />
        {!media ? (
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="story-upload-btn"
            className="w-full aspect-[9/16] max-h-[48vh] rounded-2xl border-2 border-dashed border-white/20 flex flex-col items-center justify-center gap-3 text-white/50 hover:border-white/40 hover:text-white/80 transition-all bg-white/[0.02]">
            {uploading ? <Loader2 className="w-8 h-8 animate-spin text-[#9387FE]" /> : <Plus className="w-8 h-8 text-[#FF6AD5]" />}
            <span className="text-sm font-medium">{uploading ? "Uploading media…" : "Tap to add photo or video"}</span>
          </button>
        ) : (
          <div className="relative rounded-2xl overflow-hidden bg-black max-h-[48vh] flex items-center justify-center border border-white/10">
            {media.kind === "video"
              ? <video src={abs(media.url)} className="max-h-[48vh] w-full object-contain" controls autoPlay muted playsInline />
              : <img src={abs(media.url)} alt="" className="max-h-[48vh] w-full object-contain" />}
            <button onClick={() => setMedia(null)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/80 border border-white/20 flex items-center justify-center hover:scale-105 transition"><X className="w-4 h-4" /></button>
          </div>
        )}
        {isCreator && (
          <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-3">
            <div className="text-sm font-medium text-white mb-2">
              Story visibility
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setVisibility("public")}
                className={`rounded-xl px-3 py-2.5 text-sm font-medium transition ${
                  visibility === "public"
                    ? "bg-white text-black"
                    : "bg-white/5 text-white/70 hover:bg-white/10"
                }`}
              >
                Public
                <span className="block text-[11px] mt-0.5 opacity-70">
                  Anyone can see
                </span>
              </button>

              <button
                type="button"
                onClick={() => setVisibility("followers")}
                className={`rounded-xl px-3 py-2.5 text-sm font-medium transition ${
                  visibility === "followers"
                    ? "bg-white text-black"
                    : "bg-white/5 text-white/70 hover:bg-white/10"
                }`}
              >
                Followers only
                <span className="block text-[11px] mt-0.5 opacity-70">
                  Only your followers
                </span>
              </button>
            </div>
          </div>
        )}

        <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Add a caption…" maxLength={300}
          data-testid="story-caption-input"
          className="w-full mt-4 h-11 px-4 rounded-full bg-white/5 border border-white/10 text-sm focus:outline-none focus:border-[#9387FE] transition" />
        <Button onClick={submit} disabled={posting || !media} data-testid="story-post-btn"
          className="w-full mt-4 rounded-full text-black font-semibold shadow-lg hover:opacity-95 transition" style={{ background: "linear-gradient(135deg,#9387FE,#FF6AD5)" }}>
          {posting ? <Loader2 className="w-4 h-4 animate-spin text-black" /> : <><Send className="w-4 h-4 mr-2" /> Share to story</>}
        </Button>
      </motion.div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/* Full-screen story viewer                                            */
/* ------------------------------------------------------------------ */
function StoryViewer({ groups, startGroup, onClose, onChanged }) {
  const { user } = useAuth();
  const [gi, setGi] = useState(startGroup);
  const [ii, setIi] = useState(0);
  const [progress, setProgress] = useState(0);
  
  // Analytics & Viewers combined with reactions list for own stories
  const [analyticsList, setAnalyticsList] = useState(null);

  // Interaction States
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [myReaction, setMyReaction] = useState(null);
  const [reactionCounts, setReactionCounts] = useState({});
  const [reactionLoading, setReactionLoading] = useState(false);

  // Swipe Up Reply Drawer State
  const [showReplySheet, setShowReplySheet] = useState(false);
  const touchStartY = useRef(0);

  // 2-Finger Pinch / Pan Zoom State
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const lastTouchDist = useRef(null);

  // Floating live reaction pops near reaction bar
  const [floatingReactions, setFloatingReactions] = useState([]);

  // Controls & Pause States
  const [isPaused, setIsPaused] = useState(false);
  // Start muted so mobile browsers allow autoplay.
  const [isMuted, setIsMuted] = useState(true);
  const timerRef = useRef(null);
  const videoRef = useRef(null);

  const group = groups[gi];
  const item = group?.items[ii];
  const mine = group?.is_mine;

  // Sync reactions and view counts
  useEffect(() => {
    if (!item) return;
    setMyReaction(null);
    setReactionCounts({});
    setShowReplySheet(false);
    setReplyText("");
    setIsPaused(false);
    setScale(1);
    setPan({ x: 0, y: 0 });
    setAnalyticsList(null);

    api.get(`/stories/${item.id}/reactions`)
      .then(({ data }) => {
        setMyReaction(data.my_reaction || null);
        setReactionCounts(data.reactions || {});
      })
      .catch(() => {});
  }, [item?.id]);

  const nextItem = useCallback(() => {
    if (!group) return;
    if (ii + 1 < group.items.length) { setIi(ii + 1); setProgress(0); }
    else if (gi + 1 < groups.length) { setGi(gi + 1); setIi(0); setProgress(0); }
    else onClose();
  }, [group, ii, gi, groups, onClose]);

  const prevItem = useCallback(() => {
    if (ii > 0) { setIi(ii - 1); setProgress(0); }
    else if (gi > 0) { setGi(gi - 1); setProgress(0); }
  }, [ii, gi]);

  // Auto-advance progress timer for images
  useEffect(() => {
    if (!item || isPaused || showReplySheet || analyticsList !== null) return;
    if (!mine) api.post(`/stories/${item.id}/view`).then(() => onChanged?.()).catch(() => {});
    if (item.media_kind === "video") return;

    clearInterval(timerRef.current);
    const DURATION = 5000, STEP = 50;
    let elapsed = progress * (DURATION / 100);

    timerRef.current = setInterval(() => {
      elapsed += STEP;
      const currentProgress = Math.min(100, (elapsed / DURATION) * 100);
      setProgress(currentProgress);
      if (currentProgress >= 100) {
        clearInterval(timerRef.current);
        nextItem();
      }
    }, STEP);

    return () => clearInterval(timerRef.current);
  }, [item?.id, isPaused, mine, nextItem, onChanged, progress, showReplySheet, analyticsList]);

  // Touch Handlers for Swipe & Zoom
  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      touchStartY.current = e.touches[0].clientY;
      setIsPaused(true);
    } else if (e.touches.length === 2) {
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      lastTouchDist.current = dist;
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && lastTouchDist.current) {
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      const newScale = Math.min(Math.max(1, scale * (dist / lastTouchDist.current)), 4);
      setScale(newScale);
      lastTouchDist.current = dist;
    }
  };

  const handleTouchEnd = (e) => {
    setIsPaused(false);
    if (e.changedTouches.length === 1) {
      const deltaY = touchStartY.current - e.changedTouches[0].clientY;
      if (deltaY > 80 && !mine) {
        setShowReplySheet(true);
      }
    }
    lastTouchDist.current = null;
  };

  // Reactions Handler
  const REACTIONS = ["❤️", "😂", "😮", "😢", "😡", "👍", "🔥", "⚡"];
  const handleReaction = async (reaction) => {
    if (!item || reactionLoading) return;
    const previousReaction = myReaction;
    const previousCounts = { ...reactionCounts };

    setReactionLoading(true);
    
    const newPopupId = Date.now();
    setFloatingReactions((prev) => [...prev, { id: newPopupId, emoji: reaction }]);
    setTimeout(() => {
      setFloatingReactions((prev) => prev.filter((p) => p.id !== newPopupId));
    }, 1200);

    if (previousReaction === reaction) {
      setMyReaction(null);
      setReactionCounts((prev) => ({ ...prev, [reaction]: Math.max(0, (prev[reaction] || 0) - 1) }));
    } else {
      setMyReaction(reaction);
      setReactionCounts((prev) => {
        const next = { ...prev };
        if (previousReaction) next[previousReaction] = Math.max(0, (next[previousReaction] || 0) - 1);
        next[reaction] = (next[reaction] || 0) + 1;
        return next;
      });
    }

    try {
      await api.post(`/stories/${item.id}/react`, { reaction });
      const refreshed = await api.get(`/stories/${item.id}/reactions`);
      setReactionCounts(refreshed.data.reactions || {});
      setMyReaction(refreshed.data.my_reaction || null);
    } catch (err) {
      setMyReaction(previousReaction);
      setReactionCounts(previousCounts);
      toast.error(err.response?.data?.detail || "Failed to update reaction");
    } finally {
      setReactionLoading(false);
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !item) return;
    setSendingReply(true);
    try {
      await api.post(`/stories/${item.id}/reply`, { text: replyText.trim() });
      toast.success("Reply sent to author");
      setReplyText("");
      setShowReplySheet(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send reply");
    } finally {
      setSendingReply(false);
    }
  };

  // Load analytics: fetch viewers and match with reactions so user1 🤗 , user2 appears correctly in single tab
  const loadAnalytics = async () => {
    setIsPaused(true);
    try {
      const [vRes, rRes] = await Promise.all([
        api.get(`/stories/${item.id}/viewers`),
        api.get(`/stories/${item.id}/reactions`)
      ]);
      
      const viewersList = vRes.data || [];
      // Fetch reactions details or map reactions from endpoint if available.
      // Since story_reactions collection stores reactions per user, let's fetch reaction documents or fallback safely.
      const reactionsRaw = await api.get(`/stories/${item.id}/reactions/detailed`).catch(() => ({ data: [] }));
      const userReactionMap = {};
      if (Array.isArray(reactionsRaw.data)) {
        reactionsRaw.data.forEach(r => {
          userReactionMap[r.user_id] = r.reaction;
        });
      }

      const combined = viewersList.map(v => ({
        ...v,
        reaction: userReactionMap[v.id] || null
      }));

      setAnalyticsList(combined);
    } catch (err) {
      // Fallback to viewer list with reactions mapped from local count or empty state
      try {
        const vRes = await api.get(`/stories/${item.id}/viewers`);
        setAnalyticsList(vRes.data || []);
      } catch {
        setAnalyticsList([]);
        toast.info("No analytics data available yet.");
      }
    }
  };

  const closeAnalytics = () => {
    setAnalyticsList(null);
    setIsPaused(false);
  };

  const del = async () => {
    if (!window.confirm("Delete this story?")) return;
    try { await api.delete(`/stories/${item.id}`); toast.success("Deleted"); onChanged?.(); onClose(); }
    catch { toast.error("Failed to delete"); }
  };

  if (!group || !item) return null;

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.98 }} 
      animate={{ opacity: 1, scale: 1 }} 
      exit={{ opacity: 0, scale: 0.98 }}
      transition={{ duration: 0.25, ease: "easeInOut" }}
      className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-2xl flex items-center justify-center select-none overflow-hidden" 
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      data-testid="story-viewer"
    >
      
      {/* Floating Small Reactions near bottom bar */}
      <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-50 pointer-events-none flex gap-2">
        <AnimatePresence>
          {floatingReactions.map((item) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 0, scale: 0.5 }}
              animate={{ opacity: [0, 1, 1, 0], y: -90, scale: 1.2 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.1, ease: "easeOut" }}
              className="text-4xl absolute drop-shadow-md"
            >
              {item.emoji}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Progress Bars */}
      <div className="absolute top-3 left-3 right-3 z-40 flex gap-1.5 max-w-2xl mx-auto">
        {group.items.map((it, idx) => (
          <div key={it.id} className="flex-1 h-1 rounded-full bg-white/20 overflow-hidden backdrop-blur-sm">
            <div className="h-full bg-gradient-to-r from-[#9387FE] to-[#FF6AD5] transition-[width] duration-75"
              style={{ width: idx < ii ? "100%" : idx === ii ? `${progress}%` : "0%" }} />
          </div>
        ))}
      </div>

      {/* Header Info */}
      <div className="absolute top-7 left-4 right-4 z-40 flex items-center justify-between max-w-2xl mx-auto">
        <div className="flex items-center gap-3">
          <img src={group.author.avatar_url || undefined} alt="" className="w-10 h-10 rounded-full border-2 border-[#FF6AD5] object-cover shadow-md" />
          <div>
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-bold tracking-tight">{group.author.name || group.author.username}</span>
              {group.author.verified && <span className="text-xs text-[#FF6AD5]">✓</span>}
            </div>
            <span className="text-[11px] text-white/60">@{group.author.username}</span>
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          {item.media_kind === "video" && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsMuted((v) => !v);
              }}
              className="p-2.5 rounded-full bg-white/10 border border-white/20 text-white hover:bg-white/20 transition"
            >
              {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
          )}
          {mine && (
            <button onClick={del} data-testid="story-delete-btn" className="p-2.5 rounded-full bg-white/10 border border-white/20 text-red-400 hover:bg-red-500/20 transition">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <button onClick={onClose} data-testid="story-close-btn" className="p-2.5 rounded-full bg-white/10 border border-white/20 hover:bg-white/20 transition">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Main Media with Zoom & Pan + PC Navigation Arrows */}
      <div 
        className="relative w-full max-w-[92vw] h-[calc(100dvh-110px)] sm:max-w-2xl sm:h-[82vh] flex items-center justify-center cursor-pointer overflow-hidden"
        onMouseDown={() => setIsPaused(true)}
        onMouseUp={() => setIsPaused(false)}
      >
        <motion.div 
          style={{ scale, x: pan.x, y: pan.y }}
          className="w-full h-full flex items-center justify-center transition-transform duration-100"
        >
          {item.media_kind === "video" ? (
            <video 
              ref={videoRef}
              key={item.id} 
              src={abs(item.media_url)} 
              autoPlay 
              muted={isMuted}
              playsInline
              onTimeUpdate={(e) => {
                const vid = e.target;
                if (vid.duration) setProgress((vid.currentTime / vid.duration) * 100);
              }}
              onEnded={nextItem} 
              className="block w-auto h-auto max-w-full max-h-full object-contain rounded-2xl shadow-2xl" 
            />
          ) : (
            <img key={item.id} src={abs(item.media_url)} alt="" className="block w-auto h-auto max-w-full max-h-full object-contain rounded-2xl shadow-2xl" />
          )}
        </motion.div>

        {item.caption && (
          <div className="absolute bottom-20 left-6 right-6 text-center pointer-events-none z-30">
            <span className="inline-block px-5 py-2.5 rounded-2xl bg-black/80 backdrop-blur-xl border border-white/15 text-sm font-medium shadow-2xl">
              {item.caption}
            </span>
          </div>
        )}

        <button onClick={prevItem} className="absolute left-0 top-0 h-full w-1/3 z-20" aria-label="previous" />
        <button onClick={nextItem} className="absolute right-0 top-0 h-full w-1/3 z-20" aria-label="next" />

        {/* Professional PC Navigation Buttons */}
        <button onClick={(e) => { e.stopPropagation(); prevItem(); }} className="hidden sm:flex absolute -left-16 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full glass border border-white/25 items-center justify-center hover:scale-110 active:scale-95 transition z-30 shadow-2xl bg-black/60"><ChevronLeft className="w-6 h-6" /></button>
        <button onClick={(e) => { e.stopPropagation(); nextItem(); }} className="hidden sm:flex absolute -right-16 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full glass border border-white/25 items-center justify-center hover:scale-110 active:scale-95 transition z-30 shadow-2xl bg-black/60"><ChevronRight className="w-6 h-6" /></button>
      </div>

      {/* Bottom Footer Controls */}
      <div className="absolute bottom-5 left-0 right-0 z-40 flex flex-col items-center px-4 gap-2">
        {mine ? (
          <div>
            {analyticsList === null ? (
              <button onClick={loadAnalytics} data-testid="story-viewers-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-black/80 border border-white/25 text-sm font-medium backdrop-blur-2xl hover:bg-black transition shadow-2xl">
                <Eye className="w-4 h-4 text-[#9387FE]" /> {item.view_count || 0} views & reactions analytics
              </button>
            ) : (
              <motion.div 
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
                className="max-w-lg w-[92vw] max-h-64 overflow-y-auto glass-strong rounded-3xl p-5 border border-white/20 shadow-2xl backdrop-blur-3xl"
              >
                <div className="flex items-center justify-between mb-3 border-b border-white/10 pb-2">
                  <h4 className="font-semibold text-sm text-white/90">Story Activity & Views</h4>
                  <button onClick={closeAnalytics} className="p-1 rounded-full bg-white/10 hover:bg-white/20"><X className="w-4 h-4" /></button>
                </div>

                {/* Combined Single Tab showing user1 🤗, user2, user3 😄 */}
                <div className="space-y-2.5">
                  {analyticsList.map((v) => (
                    <div key={v.id} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-none">
                      <div className="flex items-center gap-3">
                        <img src={v.avatar_url || undefined} alt="" className="w-8 h-8 rounded-full object-cover border border-white/20" />
                        <div>
                          <div className="text-sm font-medium flex items-center gap-2">
                            <span>{v.name}</span>
                            {v.reaction && <span className="text-base">{v.reaction}</span>}
                          </div>
                          <span className="text-[11px] text-white/40">@{v.username}</span>
                        </div>
                      </div>
                      <span className="text-[11px] text-[#FF6AD5] font-medium bg-[#FF6AD5]/10 px-2.5 py-0.5 rounded-full">Viewed</span>
                    </div>
                  ))}
                  {analyticsList.length === 0 && <div className="text-sm text-white/40 py-3 text-center">No views or reactions yet</div>}
                </div>
              </motion.div>
            )}
          </div>
        ) : (
          <div className="w-full max-w-lg flex flex-col items-center gap-3">
            
            {/* Direct Reaction Buttons Bar */}
            <div className="flex items-center justify-center gap-2 bg-black/75 backdrop-blur-3xl px-5 py-2.5 rounded-full border border-white/20 shadow-2xl">
              {REACTIONS.map((emoji) => {
                const count = reactionCounts[emoji] || 0;
                const selected = myReaction === emoji;
                return (
                  <button
                    key={emoji}
                    onClick={() => handleReaction(emoji)}
                    className={`relative flex items-center gap-1 px-2.5 py-1.5 rounded-full text-lg transition-all duration-200 hover:scale-125 ${
                      selected ? "bg-white/25 ring-2 ring-[#FF6AD5] scale-110 shadow-lg" : "hover:bg-white/10"
                    }`}
                  >
                    <span>{emoji}</span>
                    {count > 0 && <span className="text-[11px] font-bold text-white">{count}</span>}
                  </button>
                );
              })}
            </div>

            {/* Swipe Up or Click Reply Trigger */}
            <div 
              onClick={() => { setIsPaused(true); setShowReplySheet(true); }}
              className="text-center cursor-pointer text-xs font-medium text-white/70 flex items-center gap-1.5 hover:text-white transition pb-1 bg-black/40 px-4 py-1.5 rounded-full border border-white/10"
            >
              <MessageSquare className="w-3.5 h-3.5 text-[#9387FE]" /> Swipe up or click here to reply
            </div>
          </div>
        )}
      </div>

      {/* Swipe Up / Click Reply Bottom Sheet */}
      <AnimatePresence>
        {showReplySheet && (
          <motion.div 
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="absolute inset-x-0 bottom-0 z-50 glass-strong rounded-t-3xl p-6 border-t border-white/25 bg-black/95 backdrop-blur-3xl shadow-2xl max-w-xl mx-auto"
          >
            <div className="w-12 h-1.5 bg-white/30 rounded-full mx-auto mb-4" />
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-sm text-white/90">Reply directly to @{group.author.username}</h4>
              <button onClick={() => { setShowReplySheet(false); setIsPaused(false); }} className="p-2 rounded-full bg-white/10 hover:bg-white/20"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={handleSendReply} className="flex items-center gap-2">
              <input
                type="text"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Type your message reply..."
                autoFocus
                className="w-full h-12 px-5 rounded-full bg-white/10 border border-white/20 text-sm text-white placeholder-white/50 focus:outline-none focus:border-[#FF6AD5] transition"
              />
              <button type="submit" disabled={sendingReply || !replyText.trim()} className="px-5 h-12 rounded-full text-black font-semibold transition flex items-center justify-center shadow-lg" style={{ background: "linear-gradient(135deg,#9387FE,#FF6AD5)" }}>
                {sendingReply ? <Loader2 className="w-4 h-4 animate-spin text-black" /> : <Send className="w-4 h-4" />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/* Story bar (avatar rings) with Auto-Refresh                          */
/* ------------------------------------------------------------------ */
export default function StoryBar() {
  const { user } = useAuth();

  const [groups, setGroups] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [viewerStart, setViewerStart] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/stories");
      setEnabled(!!data.enabled);
      setGroups((data.groups || []).map((g) => ({
        ...g,
        author: {
          ...g.author,
          username: g.author?.username || g.author?.name || "User",
          avatar_url: g.author?.avatar_url || g.author?.avatar || "",
        },
        items: (g.items || []).map((item) => ({
          ...item,
          media_url: item.media_url || item.image || "",
          media_kind: item.media_kind || (item.image ? "image" : "text"),
          caption: item.caption || item.text || "",
        })),
      })));
    } catch { setGroups([]); }
  }, []);

  useEffect(() => { 
    load(); 
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [load, user?.id]);

  if (!enabled) return null;

  const myGroupIndex = groups.findIndex((g) => g.is_mine);

  return (
    <div className="mb-6" data-testid="story-bar">
      <div className="flex items-center gap-4 overflow-x-auto pb-2 no-scrollbar">
        {user && (
          <button onClick={() => (myGroupIndex >= 0 ? setViewerStart(myGroupIndex) : setShowCreate(true))}
            data-testid="story-add-btn" className="flex flex-col items-center gap-1.5 shrink-0 w-[72px] group">
            <div className="relative w-16 h-16 rounded-full p-[2px] bg-gradient-to-tr from-[#9387FE] to-[#FF6AD5] group-hover:scale-105 transition">
              {myGroupIndex >= 0 ? (
                <img src={groups[myGroupIndex].author.avatar_url || user.avatar_url || user.avatar || undefined} alt="" className="w-full h-full rounded-full object-cover border-2 border-[#07070b]" />
              ) : (
                <img src={user.avatar_url || user.avatar || undefined} alt="" className="w-full h-full rounded-full object-cover border-2 border-[#07070b] opacity-90" />
              )}
              <span onClick={(e) => { e.stopPropagation(); setShowCreate(true); }}
                className="absolute bottom-0 right-0 w-6 h-6 rounded-full flex items-center justify-center border-2 border-[#07070b] shadow-md" style={{ background: "linear-gradient(135deg,#9387FE,#FF6AD5)" }}>
                <Plus className="w-3.5 h-3.5 text-black stroke-[3]" />
              </span>
            </div>
            <span className="text-[11px] text-white/70 font-medium truncate w-full text-center">Your story</span>
          </button>
        )}

        {groups.map((g, idx) => {
          if (g.is_mine) return null;
          return (
            <button key={g.author.id} onClick={() => setViewerStart(idx)}
              data-testid={`story-ring-${g.author.username}`} className="flex flex-col items-center gap-1.5 shrink-0 w-[72px] group">
              <div className="w-16 h-16 rounded-full p-[2px] transition group-hover:scale-105 shadow-md" style={{ background: g.has_unseen ? "linear-gradient(135deg,#9387FE,#FF6AD5,#FFD54A)" : "rgba(255,255,255,0.2)" }}>
                <img src={g.author.avatar_url || undefined} alt="" className="w-full h-full rounded-full object-cover border-2 border-[#07070b]" />
              </div>
              <span className="text-[11px] text-white/70 font-medium truncate w-full text-center">{g.author.username}</span>
            </button>
          );
        })}

        {groups.length === 0 && !user && (
          <div className="text-sm text-white/40 py-4 px-2">No active stories right now.</div>
        )}
      </div>

      <AnimatePresence>
        {showCreate && <CreateStory onClose={() => setShowCreate(false)} onCreated={load} />}
      </AnimatePresence>
      {viewerStart !== null && createPortal(
        <StoryViewer
          groups={groups}
          startGroup={viewerStart}
          onClose={() => setViewerStart(null)}
          onChanged={load}
        />,
        document.body
      )}
    </div>
  );
}